import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop';
import StickyMobileCTA from './components/StickyMobileCTA/StickyMobileCTA';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

import Home        from './pages/Home/Home';
import Movie       from './pages/Movie/Movie';
import Quiz        from './pages/Quiz/Quiz';
import QuizResult  from './pages/QuizResult/QuizResult';
import Profile     from './pages/Profile/Profile';
import Challenge   from './pages/Challenge/Challenge';
import About       from './pages/About/About';
import Privacy     from './pages/Privacy/Privacy';
import Terms       from './pages/Terms/Terms';
import Support     from './pages/Support/Support';
import Discover    from './pages/Discover/Discover';
import CuratedListDetail from './pages/CuratedListDetail/CuratedListDetail';
import Login       from './pages/Login/Login';
import Register    from './pages/Register/Register';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword  from './pages/ResetPassword/ResetPassword';
import VerifyEmail    from './pages/VerifyEmail/VerifyEmail';
import MyProfile   from './pages/MyProfile/MyProfile';
import Ratings     from './pages/Ratings/Ratings';
import UserRankings from './pages/UserRankings/UserRankings';
import RankingEditor from './pages/RankingEditor/RankingEditor';
import PublicRanking from './pages/PublicRanking/PublicRanking';
import MyRankings    from './pages/MyRankings/MyRankings';
import Settings    from './pages/Settings/Settings';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import BlockedUsers from './pages/BlockedUsers/BlockedUsers';
import AccountPasswordChange from './pages/AccountSettings/AccountPasswordChange';
import Collections from './pages/Collections/Collections';
import CollectionDetail from './pages/CollectionDetail/CollectionDetail';
import PublicCollectionLanding from './pages/PublicCollectionLanding/PublicCollectionLanding';
import EnterDob from './pages/EnterDob/EnterDob';
import Followers from './pages/Followers/Followers';
import Following from './pages/Following/Following';
import PublicProfileShare from './pages/PublicProfileShare/PublicProfileShare';
import PublicCoinShare from './pages/PublicCoinShare/PublicCoinShare';
import PublicAchievementShare from './pages/PublicAchievementShare/PublicAchievementShare';
import PublicCollectionShare from './pages/PublicCollectionShare/PublicCollectionShare';
import PublicWatchlistShare from './pages/PublicWatchlistShare/PublicWatchlistShare';
import Feedback from './pages/Feedback/Feedback';
import AdminCoins from './pages/Admin/Coins';
import AdminCollections from './pages/Admin/AdminCollections';
import AdminCuratedLists from './pages/Admin/AdminCuratedLists';
import AdminEditorialPicks from './pages/Admin/AdminEditorialPicks';
import AdminAvatars from './pages/Admin/AdminAvatars';
import AdminChallengeLoaders from './pages/Admin/AdminChallengeLoaders';
import AdminCampaigns from './pages/Admin/Campaigns/AdminCampaigns';
import AdminEmails from './pages/Admin/Emails/AdminEmails';
import AdminCampaignEditor from './pages/Admin/Campaigns/AdminCampaignEditor';
import Unsubscribe from './pages/Unsubscribe/Unsubscribe';
import AdminQuizQuestions from './pages/Admin/AdminQuizQuestions';
import AdminQuizPools from './pages/Admin/AdminQuizPools';
import UserReports from './pages/Admin/UserReports';
import Onboarding from './pages/Onboarding/Onboarding';
import OnboardingVerify from './pages/Onboarding/OnboardingVerify';
import WatchlistOnboarding from './pages/Onboarding/WatchlistOnboarding';

import AdminLayout from './pages/Admin/AdminLayout';
import Overview from './pages/Admin/Overview';
import AdminFeedback from './pages/Admin/Feedback';
import QuizReports from './pages/Admin/QuizReports';
import Users from './pages/Admin/Users';
import OnboardingInsights from './pages/Admin/OnboardingInsights';
import HowItWorks from './pages/HowItWorks/HowItWorks';
import DeleteAccount from './pages/DeleteAccount/DeleteAccount';
import MyListDetail from './pages/MyListDetail/MyListDetail';
import PublicListShare from './pages/PublicListShare/PublicListShare';
import TopMovies from './pages/TopMovies/TopMovies';
import PublicTopMovies from './pages/PublicTopMovies/PublicTopMovies';
import AnalyticsRouteTracker from './analytics/AnalyticsRouteTracker';
import OfflineBanner from './components/OfflineBanner/OfflineBanner';
import VerifyQuiz from './pages/VerifyQuiz/VerifyQuiz';
import NotificationSettings from './pages/NotificationSettings/NotificationSettings';
import ActivationFunnel from './pages/Admin/ActivationFunnel';
import OnboardingGate from './components/OnboardingGate/OnboardingGate';
import { captureFirstTouch } from './attribution/firstTouch';

// First-touch attribution is captured once, on the very first page load, and
// kept until a signup persists it server-side.
captureFirstTouch();

// Admin pages have their own layout (AdminLayout sidebar / top bar); the fixed
// site navbar would sit over them and swallow clicks on their headers.
function SiteNavbar() {
    const { pathname } = useLocation();
    if (/^\/admin(\/|$)/.test(pathname)) return null;
    return <Navbar />;
}

function App() {
    return (
        <ThemeProvider>
            <BrowserRouter>
                <AuthProvider>
                    <AnalyticsRouteTracker />
                    <OnboardingGate />
                    <ScrollToTop />
                    <SiteNavbar />
                    <OfflineBanner />
                    <Routes>
                        <Route path="/"                  element={<Home />} />
                        <Route path="/login"             element={<Login />} />
                        <Route path="/register"          element={<Register />} />
                        <Route path="/forgot-password"   element={<ForgotPassword />} />
                        <Route path="/reset-password"    element={<ResetPassword />} />
                        <Route path="/verify-email"      element={<VerifyEmail />} />
                        <Route path="/unsubscribe"       element={<Unsubscribe />} />
                        <Route path="/enter-dob"         element={<EnterDob />} />
                        <Route path="/onboarding"        element={<Onboarding />} />
                        <Route path="/onboarding/verify" element={<OnboardingVerify />} />
                        <Route path="/onboarding/watchlist" element={
                            <ProtectedRoute><WatchlistOnboarding /></ProtectedRoute>
                        } />
                        {/* Canonical quiz deep links — see pages/VerifyQuiz */}
                        <Route path="/verify/movie/:tmdbId" element={<VerifyQuiz />} />
                        <Route path="/verify/tv/:tmdbId/season/:seasonNumber" element={<VerifyQuiz />} />
                        <Route path="/verify/:kind/:tmdbId" element={<VerifyQuiz />} />
                        <Route path="/feedback"          element={<Feedback />} />
                        <Route path="/movie/:kind/:tmdbId"        element={<Movie />} />
                        <Route path="/movie/tv/:tmdbId/season/:seasonNumber" element={<Movie />} />
                        <Route path="/quiz/result"        element={<QuizResult />} />
                        <Route path="/quiz/:id"           element={<Quiz />} />
                        <Route path="/quiz"               element={<Quiz />} />
                        <Route path="/u/:username"        element={<PublicProfileShare />} />
                        <Route path="/u/:username/watchlist" element={<PublicWatchlistShare />} />
                        <Route path="/l/:slug"            element={<PublicListShare />} />
                        {/* ── My Top Movies: creator works for guests; shared lists are public ── */}
                        <Route path="/top-movies"         element={<TopMovies />} />
                        <Route path="/top-movies/:slug"   element={<PublicTopMovies />} />
                        {/* Custom Rankings: create works for guests; the keys force a fresh
                            editor when a new ranking is saved and opens in edit mode. */}
                        <Route path="/rankings/new"       element={<RankingEditor key="new" />} />
                        <Route path="/rankings/:id/edit"  element={<ProtectedRoute><RankingEditor key="edit" /></ProtectedRoute>} />
                        <Route path="/rankings/:slug"     element={<PublicRanking />} />
                        <Route path="/@:username"         element={<PublicProfileShare />} />
                        <Route path="/coin/:coinId"       element={<PublicCoinShare />} />
                        <Route path="/achievement/:achievementId" element={<PublicAchievementShare />} />
                        <Route path="/collection/:collectionId"   element={<PublicCollectionShare />} />
                        <Route path="/user/:username"    element={<Profile />} />
                        <Route path="/user/:username/ratings" element={<Ratings />} />
                        <Route path="/user/:username/rankings" element={<UserRankings />} />
                        <Route path="/user/:username/followers" element={<Followers />} />
                        <Route path="/user/:username/following" element={<Following />} />
                        <Route path="/challenge/:movie"  element={<Challenge />} />
                        <Route path="/about"             element={<About />} />
                        <Route path="/privacy"           element={<Privacy />} />
                        <Route path="/terms"             element={<Terms />} />
                        <Route path="/support"           element={<Support />} />
                        <Route path="/how-it-works"      element={<HowItWorks />} />
                        <Route path="/delete-account"    element={<DeleteAccount />} />
                        <Route path="/discover"          element={<Discover />} />
                        <Route path="/discover/category/:categoryId" element={<Discover />} />
                        <Route path="/discover/lists/:slug" element={<CuratedListDetail />} />
                        {/* ── Public SEO collection landing pages (no auth required) ── */}
                        <Route path="/collections/:slug" element={<PublicCollectionLanding />} />
                        {/* ── Auth-gated in-app collection detail (protected, different path) ── */}
                        <Route path="/app/collections/:slug" element={
                            <ProtectedRoute><CollectionDetail /></ProtectedRoute>
                        } />
                        <Route path="/collections" element={<Collections />} />
                        <Route path="/me/rankings" element={
                            <ProtectedRoute><MyRankings /></ProtectedRoute>
                        } />
                        <Route path="/me/ratings" element={
                            <ProtectedRoute><Ratings /></ProtectedRoute>
                        } />
                        <Route path="/me" element={
                            <ProtectedRoute><MyProfile /></ProtectedRoute>
                        } />
                        {/* ── My Lists (user-created custom lists; private, auth-only) ── */}
                        <Route path="/lists/:listId" element={
                            <ProtectedRoute><MyListDetail /></ProtectedRoute>
                        } />
                        <Route path="/settings" element={
                            <ProtectedRoute><Settings /></ProtectedRoute>
                        } />
                        <Route path="/settings/account" element={
                            <ProtectedRoute><AccountSettings /></ProtectedRoute>
                        } />
                        <Route path="/settings/password" element={
                            <ProtectedRoute><AccountPasswordChange /></ProtectedRoute>
                        } />
                        <Route path="/settings/blocked-users" element={
                            <ProtectedRoute><BlockedUsers /></ProtectedRoute>
                        } />
                        <Route path="/settings/notifications" element={
                            <ProtectedRoute><NotificationSettings /></ProtectedRoute>
                        } />
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<Overview />} />
                            <Route path="feedback"     element={<AdminFeedback />} />
                            <Route path="quiz-reports" element={<QuizReports />} />
                            <Route path="users"        element={<Users />} />
                            <Route path="onboarding"   element={<OnboardingInsights />} />
                            <Route path="activation"   element={<ActivationFunnel />} />
                            <Route path="coins"        element={<AdminCoins />} />
                            <Route path="collections"  element={<AdminCollections />} />
                            <Route path="curated-lists" element={<AdminCuratedLists />} />
                            <Route path="editorial-picks" element={<AdminEditorialPicks />} />
                            <Route path="avatars" element={<AdminAvatars />} />
                            <Route path="challenge-loaders" element={<AdminChallengeLoaders />} />
                            <Route path="campaigns" element={<AdminCampaigns />} />
                            {/* One route for /new and /:id so saving a new draft (URL /new → /:id) keeps the editor mounted. */}
                            <Route path="campaigns/:id" element={<AdminCampaignEditor />} />
                            <Route path="emails" element={<AdminEmails />} />
                            <Route path="quiz-questions" element={<AdminQuizQuestions />} />
                            <Route path="quiz-pools" element={<AdminQuizPools />} />
                            <Route path="user-reports" element={<UserReports />} />
                        </Route>
                    </Routes>
                    <Footer />
                    <StickyMobileCTA />
                </AuthProvider>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
